import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { ContentHeader, EmptyState, Field, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type {
  AccountantSettingsView,
  AccountingFileLink,
  FiscalFileList,
  FiscalFileView,
  SpedSettings,
} from "@cx/contracts";
import {
  FISCAL_FILE_KINDS,
  FISCAL_FILE_LABELS,
  formatBRL,
  shiftCompetence,
  todayInSaoPaulo,
  type FiscalFileKind,
} from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { dateTime } from "../../format";
import { useSession } from "../../session";
import {
  FILE_STATUS_LABEL,
  FILE_STATUS_TONE,
  PURPOSES,
  competenceLabel,
  competenceOptions,
  sizeLabel,
} from "./labels";

/**
 * Contabilidade › Arquivos fiscais (T3.5, RF-CTB-02): SPED Fiscal (EFD ICMS/IPI), SPED Contribuições e Sintegra do mês,
 * gerados em segundo plano com os registros que o ERP preenche. Arquivo pronto não muda (gere de novo para
 * substituir). Validar no PVA/validador oficial antes de transmitir.
 */

const MONTHS = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

/** Totais conhecidos do resumo, em reais. */
const TOTAL_LABELS: Record<string, string> = {
  debitsCents: "Débitos de ICMS",
  creditsCents: "Créditos de ICMS",
  toPayCents: "ICMS a recolher",
  creditCarryCents: "Saldo credor a transportar",
  ipiDebitsCents: "Débitos de IPI",
  inventoryCents: "Inventário",
  pisCents: "PIS",
  cofinsCents: "COFINS",
};

function SpedSettingsForm({ sped, onClose }: { sped: SpedSettings; onClose(): void }): ReactElement {
  const { api, companyId } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [v, setV] = useState(sped);
  const save = useMutation({
    mutationFn: () =>
      api<AccountantSettingsView>("/accounting/settings", {
        method: "PUT",
        body: JSON.stringify({ sped: v }),
      }),
    onSuccess: (s) => {
      qc.setQueryData(["accounting", companyId, "settings"], s);
      toast("success", "Configuração do SPED salva");
      onClose();
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  return (
    <div className="row g-2 p-3 border-bottom" data-testid="sped-settings">
      <Field label="Perfil" className="col-md-2" hint="Confira com o contador.">
        <select
          className="form-select"
          value={v.profile}
          onChange={(e) => setV({ ...v, profile: e.target.value as SpedSettings["profile"] })}
        >
          <option value="A">A</option>
          <option value="B">B</option>
          <option value="C">C</option>
        </select>
      </Field>
      <Field label="Código de receita do ICMS" className="col-md-3" hint="Tabela da UF (E116).">
        <input
          className="form-control"
          value={v.icmsRevenueCode ?? ""}
          maxLength={20}
          onChange={(e) => setV({ ...v, icmsRevenueCode: e.target.value || null })}
        />
      </Field>
      <Field label="Vencimento do ICMS (dia)" className="col-md-2">
        <input
          type="number"
          min={1}
          max={31}
          className="form-control"
          value={v.icmsDueDay}
          onChange={(e) => setV({ ...v, icmsDueDay: Number(e.target.value) || 10 })}
        />
      </Field>
      <Field label="Inventário (bloco H / 74) no mês" className="col-md-3">
        <select
          className="form-select"
          value={v.inventoryMonth ?? ""}
          onChange={(e) => setV({ ...v, inventoryMonth: e.target.value ? Number(e.target.value) : null })}
        >
          <option value="">Nunca (só quando pedir)</option>
          {MONTHS.map((m, k) => (
            <option key={m} value={k + 1}>
              {m}
            </option>
          ))}
        </select>
      </Field>
      <div className="col-md-2 d-flex align-items-end mb-3">
        <button
          type="button"
          className="btn btn-primary w-100"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          Salvar
        </button>
      </div>
    </div>
  );
}

function FileRow({ f }: { f: FiscalFileView }): ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const download = useMutation({
    mutationFn: () => api<AccountingFileLink>(`/accounting/files/${f.id}/download`),
    onSuccess: (r) => window.location.assign(r.url),
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const totals = Object.entries(f.summary?.totals ?? {}).filter(
    (e): e is [string, number] => typeof e[1] === "number" && e[0] in TOTAL_LABELS,
  );
  const warnings = f.summary?.warnings ?? [];
  return (
    <tr data-testid={`fiscal-file-${f.kind}`}>
      <td className="text-nowrap">
        {FISCAL_FILE_LABELS[f.kind]}
        <div className="small text-muted">
          {PURPOSES[f.kind].find((p) => p.value === f.purpose)?.label ?? f.purpose}
        </div>
      </td>
      <td>{competenceLabel(f.competence)}</td>
      <td>
        <StatusBadge tone={FILE_STATUS_TONE[f.status]}>{FILE_STATUS_LABEL[f.status]}</StatusBadge>
        {f.error && <div className="small text-danger">{f.error}</div>}
      </td>
      <td className="small">
        {f.recordsCount !== null && <div>{f.recordsCount} linhas</div>}
        {totals.map(([k, val]) => (
          <div key={k}>
            {TOTAL_LABELS[k]}: {formatBRL(val)}
          </div>
        ))}
        {warnings.length > 0 && (
          <details className="text-muted">
            <summary>{warnings.length} aviso(s)</summary>
            <ul className="mb-0">
              {warnings.slice(0, 50).map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </details>
        )}
      </td>
      <td className="small text-nowrap">{dateTime(f.createdAt)}</td>
      <td className="small">{sizeLabel(f.sizeBytes)}</td>
      <td className="text-end">
        {f.status === "ready" && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={download.isPending}
            onClick={() => download.mutate()}
          >
            Baixar
          </button>
        )}
      </td>
    </tr>
  );
}

export function FiscalFilesPage(): ReactElement {
  const { api, companyId, me } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const [kind, setKind] = useState<FiscalFileKind>("sped_fiscal");
  const [competence, setCompetence] = useState(shiftCompetence(todayInSaoPaulo().slice(0, 7), -1));
  const [purpose, setPurpose] = useState(0);
  const [inventory, setInventory] = useState<"auto" | "yes" | "no">("auto");
  const [inventoryDate, setInventoryDate] = useState("");
  const [config, setConfig] = useState(false);
  const [key, setKey] = useState(() => crypto.randomUUID());
  const settings = useQuery({
    queryKey: ["accounting", companyId, "settings"],
    queryFn: () => api<AccountantSettingsView>("/accounting/settings"),
  });
  const files = useQuery({
    queryKey: ["accounting", companyId, "files"],
    queryFn: () => api<FiscalFileList>("/accounting/files"),
    refetchInterval: (q) => (q.state.data?.data.some((f) => f.status === "queued") ? 2_000 : false),
  });
  const generate = useMutation({
    mutationFn: () =>
      api<FiscalFileView>("/accounting/files", {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({
          kind,
          competence,
          purpose,
          withInventory: inventory === "auto" ? null : inventory === "yes",
          inventoryDate: inventory === "yes" && inventoryDate ? inventoryDate : null,
        }),
      }),
    onSuccess: (f) => {
      toast("success", `${FISCAL_FILE_LABELS[f.kind]} de ${competenceLabel(f.competence)} em geração`);
      setKey(crypto.randomUUID());
      void qc.invalidateQueries({ queryKey: ["accounting", companyId, "files"] });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const s = settings.data;
  const rows = files.data?.data ?? [];
  return (
    <>
      <ContentHeader
        title="Arquivos fiscais"
        breadcrumb={["Contabilidade", "Arquivos fiscais"]}
        actions={
          can(perms, "accounting", "update") && s ? (
            <button type="button" className="btn btn-outline-secondary" onClick={() => setConfig((x) => !x)}>
              Configurar SPED
            </button>
          ) : undefined
        }
      />
      <QueryStatus loading={settings.isLoading} error={settings.error} />
      {s?.company.isMei && (
        <div className="alert alert-info">
          MEI não entrega SPED nem Sintegra. Use o <strong>DAS MEI</strong> e a declaração anual (DASN-SIMEI).
        </div>
      )}
      {s && !s.accountant && !s.company.isMei && (
        <div className="alert alert-warning">
          O SPED pede os dados do contador (registro 0100): cadastre-o no Espaço Meu Contador.
        </div>
      )}
      <div className="box card mb-3">
        {config && s && <SpedSettingsForm sped={s.sped} onClose={() => setConfig(false)} />}
        {can(perms, "accounting", "create") && (
          <div className="row g-2 p-3 align-items-end">
            <Field label="Arquivo" className="col-md-3">
              <select
                className="form-select"
                value={kind}
                data-testid="fiscal-file-kind"
                onChange={(e) => {
                  const k = e.target.value as FiscalFileKind;
                  setKind(k);
                  setPurpose(PURPOSES[k][0]!.value);
                }}
              >
                {FISCAL_FILE_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {FISCAL_FILE_LABELS[k]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Competência" className="col-md-2">
              <select
                className="form-select"
                value={competence}
                data-testid="fiscal-file-competence"
                onChange={(e) => setCompetence(e.target.value)}
              >
                {competenceOptions().map((c) => (
                  <option key={c} value={c}>
                    {competenceLabel(c)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Finalidade" className="col-md-2">
              <select
                className="form-select"
                value={purpose}
                onChange={(e) => setPurpose(Number(e.target.value))}
              >
                {PURPOSES[kind].map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </Field>
            {kind !== "sped_contribuicoes" && (
              <Field
                label="Inventário"
                className="col-md-2"
                hint={s?.sped.inventoryMonth ? `Padrão: em ${MONTHS[s.sped.inventoryMonth - 1]}` : undefined}
              >
                <select
                  className="form-select"
                  value={inventory}
                  onChange={(e) => setInventory(e.target.value as typeof inventory)}
                >
                  <option value="auto">Pelo mês configurado</option>
                  <option value="yes">Incluir</option>
                  <option value="no">Não incluir</option>
                </select>
              </Field>
            )}
            {inventory === "yes" && kind !== "sped_contribuicoes" && (
              <Field label="Data do inventário" className="col-md-2" hint="Padrão: 31/12 do ano anterior.">
                <input
                  type="date"
                  className="form-control"
                  value={inventoryDate}
                  max={todayInSaoPaulo()}
                  onChange={(e) => setInventoryDate(e.target.value)}
                />
              </Field>
            )}
            <div className="col-md-1 mb-3">
              <button
                type="button"
                className="btn btn-primary w-100"
                disabled={generate.isPending || !!s?.company.isMei}
                onClick={() => generate.mutate()}
              >
                Gerar
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="box card">
        <QueryStatus loading={files.isLoading} error={files.error} />
        {files.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhum arquivo gerado"
            description="Escolha o arquivo e a competência e clique em Gerar. O arquivo sai em segundo plano."
            actions={[]}
          />
        )}
        {rows.length > 0 && (
          <div className="table-responsive">
            <table className="table align-middle mb-0" aria-label="Arquivos fiscais">
              <thead>
                <tr>
                  <th scope="col">Arquivo</th>
                  <th scope="col">Competência</th>
                  <th scope="col">Situação</th>
                  <th scope="col">Resumo</th>
                  <th scope="col">Gerado em</th>
                  <th scope="col">Tamanho</th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <FileRow key={f.id} f={f} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="small text-muted p-3 mb-0">
          Os arquivos trazem os registros que o ERP preenche (NF-e, NFC-e, NFS-e, notas de entrada, apuração e
          inventário). Valide no PVA da Receita (SPED) ou no validador do Sintegra antes de transmitir; regras
          do seu regime podem pedir ajustes do contador.
        </p>
      </div>
    </>
  );
}
